import React, { useCallback, useContext, useEffect, useState } from "react";
import InnerLayout from "../../components/layouts/inner-layout";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../components/tables/dynamic-table1";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import { selfDeclarationTableAction } from "../../redux/actions/store-action";
import StoreFilter from "../../components/tables/table-filter/store-filter";
import { DataContext } from "../../components/layouts/main-layout";
import { useHotkeys } from "react-hotkeys-hook";
import EyeOpenIcon from "../../assets/icons/eye-open-icon";
import { declarationStatusLabel } from "../../components/store-information/self-declaration/self-declaration-view";

const yesNo = (value) => (value == null ? "--" : value ? "Yes" : "No");

const SelfDeclaration = () => {
  const { permissionFn } = useContext(DataContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [declarationList, setDeclarationList] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [tableHeading, setTableHeading] = useState(tabHeading);
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    selectColumn: "franchisee_name",
  });

  useEffect(() => {
    dispatch(
      selfDeclarationTableAction(
        {
          pageNumber: currentPage,
          pageSize: pageSize,
          filterData,
        },
        onSuccess,
        onError
      )
    );
  }, [currentPage, pageSize, filterData, reload]);
  const onSuccess = (data) => {
    setError(false);
    setLoading(false);
    setDeclarationList(data?.data?.declarations ?? []);
    setDataCount(data?.data?.pagination?.totalCount ?? 0);
  };
  const onError = () => {
    setError(true);
    setLoading(false);
  };

  const filteredTableHeading = tableHeading.filter(
    (item) => item.isColumnneeded
  );

  const tableBodyData = declarationList?.map((item) => {
    const rowData = { id: item?.store_declaration_id };
    filteredTableHeading.forEach((column) => {
      if (column.value === "franchisee_name") {
        rowData.franchisee_name = (
          <span className="fw_500">{item?.franchisee_name ?? "--"}</span>
        );
      } else if (column.value === "mobile_number") {
        rowData.mobile_number = item?.mobile_number
          ? `+91 ${item.mobile_number}`
          : "--";
      } else if (column.value === "store_type") {
        // getAll does not return store_type yet; firm_type fills the column
        // until it does.
        rowData.store_type = item?.store_type || item?.firm_type || "--";
      } else if (column.value === "has_relative_in_company") {
        rowData.has_relative_in_company = yesNo(item?.has_relative_in_company);
      } else if (column.value === "status") {
        rowData.status = declarationStatusLabel(item);
      } else if (column.value === "action") {
        rowData.action = (
          <div
            className="flex items-center gap-3 justify-center w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              title="View"
              className="flex items-center hover:opacity-70"
              onClick={() =>
                navigate(`/view-self-declaration/${item?.store_declaration_id}`)
              }
            >
              <EyeOpenIcon fill="#98A2B3" />
            </button>
          </div>
        );
      } else {
        rowData[column.value] = item?.[column.value] || "--";
      }
    });
    return rowData;
  });
  const paginationProps = {
    pageSize: pageSize,
    setPageSize: setPageSize,
    dataCount: dataCount,
    setCurrentPage: setCurrentPage,
    currentPage: currentPage,
  };
  const handleFilterClick = useCallback(() => {
    setShowFilter((prevShowFilter) => !prevShowFilter);
  }, []);

  const filterby = showFilter ? (
    <StoreFilter
      setTableHeading={setTableHeading}
      tableHeading={tableHeading}
      filterData={filterData}
      setFilterData={setFilterData}
      setShowFilter={setShowFilter}
    />
  ) : null;

  useHotkeys("escape", () => {
    setShowFilter(false);
  });

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="store_document_process"
      list={true}
      title="Self Declaration"
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <></>
      ) : (
        <DynamicTable
          is_checkbox_req={false}
          list={false}
          menu={false}
          permissionFn={permissionFn}
          module={"store_document_process"}
          date={false}
          path="self-declaration"
          id_name="store_declaration_id"
          reload={reload}
          setReload={setReload}
          loading={loading}
          setLoading={setLoading}
          label="Self Declaration"
          showFilter={showFilter}
          filterFn={handleFilterClick}
          setFilterData={setFilterData}
          filterData={filterData}
          paginationProps={paginationProps}
          tableHeading={filteredTableHeading}
          tableBodyData={tableBodyData}
          placeholder="Search"
          filterBy={filterby}
          filterText="Show or Hide Columns"
          initialFilter={{
            sort_by: "",
            sort_order: "",
            selectColumn: "franchisee_name",
          }}
        />
      )}
    </InnerLayout>
  );
};

export default SelfDeclaration;
const tabHeading = [
  {
    label: "CONTACT PERSON",
    value: "franchisee_name",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "MOBILE NO",
    value: "mobile_number",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "STORE TYPE",
    value: "store_type",
    display: false,
    column: false,
    isColumnneeded: true,
  },
  {
    label: "FIRM NAME",
    value: "firm_name",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "DESIGNATION",
    value: "designation",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "PLACE",
    value: "place",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "ANY RELATION IN ZOTA",
    value: "has_relative_in_company",
    display: false,
    column: false,
    isColumnneeded: true,
  },
  {
    label: "STATUS",
    value: "status",
    display: true,
    column: false,
    isColumnneeded: true,
  },
  {
    label: "ACTION",
    value: "action",
    display: false,
    column: false,
    isColumnneeded: true,
  },
];
